<template>
    <div class="news">
        <div class="row">
            <div class="news__filters-container col-default-12" :class="{'news__filters-container--open': params.category !== 'all' || params.from || params.to || params.type !== 'all'}">
                <div class="news__filters">
                    <z-select
                        class="news__filters-item col-default-4 col-tablet-12"
                        @change="selectNews"
                        :data="newsFilter"
                        :value="params.type"
                        :placeholder="text.filters.type"
                    />
                    <z-select
                        class="news__filters-item col-default-4 col-tablet-12"
                        @change="selectCategory"
                        :data="categoryFilter"
                        :value="params.category"
                        :placeholder="text.filters.category"
                    />
                    <z-date-picker
                        range
                        class="news__filters-item col-default-4 col-tablet-12"
                        @change="selectDate"
                        :placeholder="text.filters.date"
                        v-model="filterDate"
                    />
                </div>
                <button
                    v-if="
                        params.category !== '' ||
                        params.from ||
                        params.to ||
                        params.type !== ''
                    "
                    class="news__filters-clear"
                    @click="clearFilters"
                >
                    {{ text.filters.reset }}
                </button>
            </div>

            <z-caption
                v-if="
                    params.category !== '' ||
                    params.from ||
                    params.to ||
                    params.type !== ''
                "
                tag="p"
                size="xs"
                class="news__count col-default-12"
                >{{ text.count }} {{ nav.count }}</z-caption
            >

            <a
                v-if="homePage"
                :href="isFileCanBeDownloaded ? homePage.file.link : homePage.detailPageUrl"
                class="news__hero news__hero--figure col-default-12"
            >
                <big-figure
                    theme="news"
                    class="news__hero"
                    :image="homePage.previewPicture ? homePage.previewPicture.link : ''"
                    position-x="left"
                    position-y="top"
                >
                    <template>
                        <date-block
                            size="l"
                            class="news__date news__date--main"
                            :date="homePageNewsDate"
                        />
                        <z-caption class="news__title" tag="h2">
                            {{ homePage.name }}
                            <span v-if="homePage.file.link" class="news__hero-tag">PDF</span>
                        </z-caption>
                        <div class="news__tags">
                            <tag
                                v-if="
                                    homePage.type.value === 'Пресс-релиз' ||
                                        homePage.type.value === 'Press Release'
                                "
                                class="news__tags-item"
                                theme="peach"
                                >{{ homePage.type.value }}</tag
                            >
                            <tag v-else class="news__tags-item" theme="white">{{
                                homePage.type.value
                            }}</tag>
                            <tag
                                v-for="(tag, index) in homePage.category"
                                :key="index"
                                class="news__tags-item"
                                theme="white"
                                >{{ tag.value }}</tag
                            >
                        </div>
                    </template>
                </big-figure>
            </a>

            <div v-if="news.length">
                <news-card v-for="(newsCard, index) in news" :key="index" :newsInfo="newsCard" />

                <z-pagination
                    @change="loadAnotherPage"
                    class="news__pagination"
                    show-next
                    show-prev
                    :data="{
                        currentPage: nav.current,
                        totalItems: nav.count,
                        totalPages: nav.total,
                        pageRange: 1
                    }"
                />
            </div>

            <z-not-found v-else>
                <template>
                    <z-caption class="news__title news__title--not-found" tag="h2">
                        {{ text.notFound.title }}
                    </z-caption>
                    <z-caption class="news__text" tag="p">
                        {{ text.notFound.text }}
                    </z-caption>
                </template>
            </z-not-found>
        </div>
    </div>
</template>

<script>
import { getNews } from '@/api/news'
import { debounce } from 'throttle-debounce'
import NewsCard from './NewsCard.vue'
import { localize } from '@/utils/i18n.js'
import moment from 'moment'
import { showNotyfications } from '@/utils/notifications'
import { queryString } from '@/utils/queryString'

export default {
    name: 'news-page',
    components: {
        NewsCard
    },
    props: {
        source: {
            type: String,
            default: 'news'
        },
        initial: {
            type: Object
        }
    },

    data () {
        return {
            loading: false,
            filterDate: null,
            params: {
                from: '',
                to: '',
                type: 'all',
                category: 'all',
                page: 1
            },
            page: 1,
            news: [],
            nav: {},
            filters: {},
            categoryFilter: [],
            newsFilter: [],
            homePage: null,
            text: {
                filters: {
                    type: localize({
                        ru: 'Пресс-релизы и новости',
                        en: 'Press releases and news'
                    }),
                    category: localize({
                        ru: 'Категория',
                        en: 'Category'
                    }),
                    date: localize({
                        ru: 'Дата публикаций',
                        en: 'Publication date'
                    }),
                    reset: localize({
                        ru: 'Сбросить',
                        en: 'Reset'
                    })
                },
                count: localize({
                    ru: 'Всего:',
                    en: 'Total:'
                }),
                notFound: {
                    title: localize({
                        ru: 'По вашему запросу ничего не найдено',
                        en: 'Nothing found for your request'
                    }),
                    text: localize({
                        ru: 'Попробуйте изменить параметры поиска',
                        en: 'Try changing your search options'
                    })
                }
            }
        }
    },

    created () {
        this.updateNewsList()
        const {kind, ...initial} = this.initial
        initial.type = kind
        this.params = {...this.params, ...initial}
    },
    mounted () {
        if (this.params.from && this.params.to) {
            this.$nextTick(() => {
                this.filterDate = [this.params.from, this.params.to]
            })
        }
    },

    computed: {
        homePageNewsDate () {
            const date = moment.unix(this.homePage.activeFrom).format('MM/DD/YYYY')
            return date
        },
        isFileCanBeDownloaded () {
            return this.homePage.file.link && !this.homePage.detailText
        },
        clearEmptyParams () {
            const {page, ...params} = this.params
            params.kind = this.params.type

            Object.keys(params).forEach(function (key, index) {
                if (key === 'date' && !params[key][0]) delete params[key]
                if (Array.isArray(params[key])) {
                    if (!params[key].length) delete params[key]
                } else if (!params[key]) {
                    delete params[key]
                }
            })

            return params
        }
    },

    watch: {
        params: {
            handler () {
                queryString(this.clearEmptyParams)
                this.loadNews()
            },
            deep: true
        }
    },

    methods: {
        updateNewsList () {
            if (this.$root.app.components[this.source]) {
                this.news = this.$root.app.components[this.source].items.filter(
                    (n) => n.homePage.value !== 'Y'
                )
                this.homePage = this.$root.app.components[this.source].items.find(
                    (post) => post.homePage.value === 'Y'
                )
                this.filters = this.$root.app.components[this.source].filter
                this.nav = this.$root.app.components[this.source].nav
                this.categoryFilter = this.filters.category
                this.newsFilter = this.filters.type
            }
        },

        selectDate (selectedDate) {
            if (selectedDate) {
                this.params.from = selectedDate[0]
                this.params.to = selectedDate[1]
            } else {
                this.params.from = ''
                this.params.to = ''
            }
        },

        selectNews (selectedNews) {
            this.params.type = selectedNews
        },

        selectCategory (selectedCategory) {
            this.params.category = selectedCategory
        },

        clearFilters () {
            this.filterDate = null
            this.params.from = null
            this.params.to = null
            this.params.type = ''
            this.params.category = ''
            this.params.page = 1
        },

        loadAnotherPage (page) {
            this.params.page = page
        },

        loadNews: debounce(500, async function () {
            if (this.loading) {
                return
            }

            try {
                this.loading = true

                const result = await getNews(this.params)

                this.news = result.items
                this.nav = result.nav
                this.homePage = result.items.find(
                    (post) => post.homePage.value === 'Y'
                )
            } catch (error) {
                showNotyfications('Ошибка загрузки', { type: 'error' })
            } finally {
                this.loading = false
            }
        })
    }
}
</script>

<style lang="scss">
.news {
    .big-figure__image {
        position: absolute !important;
    }

    &__filters-container {
        margin-bottom: $token-spacers-2-xl;

        &--open {
           margin-bottom: $token-spacers-m;
        }
    }

    &__filters {
        display: flex;
        justify-content: space-between;
        width: 100%;
        flex-wrap: wrap;

        &-item {
            margin-bottom: $token-spacers-2-xs;
            padding: 0;
        }

        &-item:not(:last-child) {
            padding-right: 24px;

            @include breakpoint(tablet) {
                padding: 0;
            }
        }

        &-clear {
            display: flex;
            justify-content: flex-end;
            width: 100%;
            font-weight: 400;
            font-size: 16px;
            line-height: 140%;
            outline: none;
            border: none !important;
            background-color: transparent;
            cursor: pointer;
            color: $token-colors-link-default;
        }
    }

    &__count {
        font-size: 18px;
        line-height: 140%;
        color: $token-colors-black-60;
        margin-bottom: $token-spacers-m;
        margin-top: 0 !important;
    }

    &__hero {
        text-decoration: none;
        cursor: pointer;

        &:hover {
            .news__title {
                color: $token-colors-link-hover !important;
            }
        }

        &--figure {
            margin-top: 0 !important;
            margin-bottom: $token-spacers-m;

            @include breakpoint(tablet) {
                margin-bottom: 0;
            }

            @include breakpoint(mobile) {
                margin-bottom: $token-spacers-m;
            }
        }

        &-tag {
            margin-left: 10px;
            background: $token-colors-tertiary-15;
            border-radius: 6px;
            padding: 2px 10px;
            color: $token-colors-link-default;
            font-size: 14px;
            line-height: 150%;
            font-weight: 500;
            vertical-align: middle;
        }
    }

    &__date {
        margin-bottom: $token-spacers-xs;

        &--main {
            @include breakpoint(mobile) {
                .date-block__icon {
                    width: 43px;
                    height: 43px;
                }

                .date-block__date {
                    font-size: 64px;
                }

                .date-block__ym-wrapper {
                    font-size: 24px;
                }
            }
        }
    }

    &__title {
        margin-bottom: $token-spacers-xs;
        font-size: 32px !important;
        line-height: 130% !important;
        color: $token-colors-link-default !important;
        font-weight: 600 !important;

        @include breakpoint(tablet) {
            font-size: 24px !important;
        }

        @include breakpoint(mobile) {
            font-size: 20px !important;
        }

        &--not-found {
            font-size: 44px !important;
            line-height: 120% !important;
            color: #2f2e34 !important;
            margin-bottom: 32px;

            @include breakpoint(tablet) {
                font-size: 24px !important;
            }

            @include breakpoint(mobile) {
                font-size: 20px !important;
            }
        }
    }

    .news-card:hover {
        .news-card__title {
            color: $token-colors-link-hover !important;
        }
    }

    &__text {
        font-weight: 600 !important;
        font-weight: 400 !important;
        font-size: 16px !important;
        line-height: 140% !important;
        color: #303033 !important;

        @media screen and (max-width: 725px) {
            max-width: 250px;
        }

        @include breakpoint(mobile) {
            max-width: 240px !important;
        }
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;

        &-item {
            margin-bottom: 12px;
        }

        &-item:not(:last-child) {
            margin-right: 10px;
        }
    }

    &__pagination {
        display: flex;
        justify-content: center;
        margin-top: $token-spacers-2-xl;
    }
}
</style>
